import { DEFAULT } from './../../../../../../shared/constants/defaults';
import { Common } from './../../../../../../shared/domain/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EMPTY,
  forkJoin,
  from,
  merge,
  Observable,
  of,
  Subscription,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  shareReplay,
  skip,
  switchMap,
  tap,
  toArray,
} from 'rxjs/operators';
import {
  CountryInterface,
  LanguageInterface,
} from '../../../../../../shared/interfaces/create-surveys.interface';
import { Constants as CreateSurveyConstants } from '../../../../../../shared/constants/create-survey.constants';
import { BuyerApiService } from '../../../../../../shared/services/buyer-api/survey.service';
import { notifyMessage } from '../../../../../../shared/constants/notify-message';
import { SURVEY_STATUS } from '../../../../../../shared/constants/survey-status';
import {
  BuyerSurveyResponse,
  PatchSurveyInterface,
  SurveyNoTargetingResponse,
} from '../../../../../../shared/interfaces/survey.interface';
import { ModalService } from '@purespectrum1/ui/modal';
import { CreateSurveyService } from '../../../../../create-survey.service';
import { Constants } from '../../../../../../shared/constants/dashboard-constants';
import { ChangeSurveyStatusModalComponent } from '../../../../../../shared/ui/change-survey-status-modal/change-survey-status-modal.component';
import { MultiCountryDraftService } from '../../../../../services/multi-country-draft/multi-country-draft.service';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { FeasibilityDataService as FeasibilityComponentService } from '../../../../../services/feasibility/feasibility-data.service';
import { AuthService } from '../../../../../../shared/services/auth/auth.service';
import { QualificationsService } from '../../../../../services/qualification/qualifications.service';
import { SurveyMetaDataService } from '../../../../../../shared/services/survey-meta-data/survey-meta-data.service';
import {
  McSurveyRequest,
  MultiCountrySurvey,
} from '../../interface/multi-country.interface';
import {
  MultiCountryNewSurveyRequestMapper,
  MultiCountrySameCountryExclusionPayloadMapper,
  MultiCountryTemplateBasedSurveyRequestMapper,
} from '../../domain/multi-country-traditional-view-data-mapper';
import { MultiCountryService } from '../../services/multi-country.service';
import { LayoutConstants } from '../../../../../layout/layout-constants';
import { TableExpandableRowDirective } from '@purespectrum1/ui/table';
import { MultiCountrySurveyFeasDataMapper } from '../../domain/multi-country-common-data-mapper';
import { TemplateService } from '../../../../../../shared/services/template/template.service';
import { DraftSurveyConfirmationModalComponent } from '../../../../../../create-surveys/draft-survey-confirmation-modal/draft-survey-confirmation-modal.component';
import {
  Template,
  TemplateResponse,
} from '../../../../../../create-surveys/layout/template-modal/template-modal.interface';
import { LocaleFullNameMapper } from '../../../../../../shared/services/locale/locale-full-name-mapper';
import { MCTemplateSelectionDataMapper } from '../../domain/mc-template-selection-data-mapper';
import { ConfirmationModalComponent } from '../../../../../../shared/ui/confirmation-modal/confirmation-modal.component';
import { SurveyPayload } from '../../../../../../shared/interfaces/survey-meta-data.interface';
import { McTraditionalSettings } from '../../domain/mc-traditional-survey-settings-composer';
import { SurveyUpdateError } from '../../../../../../shared/services/buyer-api/domain/survey-update-error';
import { McQuotaQualificationActions } from '../../domain/mc-quota-qualification-data-mapper';
import { SurveyPayloadService } from '../../../../../../create-surveys/survey-payload.service';
import { Qualification } from '../../../../../../shared/interfaces/qualification.interface';
import { AidaSurveyPayloadTransformer } from './../../../../../../survey-launch/ai-delivery-assistant/domain/survey-payload-transformer-for-aida';
import { AidaPayloadService } from './../../../../../../shared/services/aida/aida-payload/aida-payload.service';

@Component({
  selector: 'ps-ui-multi-country-traditional-view',
  templateUrl: './multi-country-traditional-view.component.html',
  styleUrls: ['./multi-country-traditional-view.component.css'],
})
export class MultiCountryTraditionalViewComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() surveyDetails?: BuyerSurveyResponse;
  @Input() surveyPayload = <SurveyPayload>{};
  @Input() filteredCountries: CountryInterface[] = [];
  @Input() multiCountrySurveyData: MultiCountrySurvey[] = [];
  @Input() isCpiEditable: boolean = false;
  @Input() allCountries: CountryInterface[] = [];
  @Input() selectedTemplateId?: number | null;

  @Output() readonly selectionChange = new EventEmitter();
  @Output() mcSurveyAdded = new EventEmitter();
  @Output() updateMultiCountrySurveyData = new EventEmitter<
    MultiCountrySurvey[]
  >();
  @Output() cancelTemplateSelection = new EventEmitter();

  @ViewChild(TableExpandableRowDirective)
  public expandable!: TableExpandableRowDirective;

  private _subscriptions: Subscription = new Subscription();
  private _selectedLanguage: LanguageInterface = {} as LanguageInterface;
  private _isOperator: boolean = false;
  private _mcLaunchSettings: McTraditionalSettings[] = [];
  private _allMcTraditionalSurveys: {
    [surveyId: number]: BuyerSurveyResponse;
  } = {};

  private _surveySelectionCommand$ = new EventEmitter<number>();
  private _updateSurveysAndSelectCommand$ = new EventEmitter<number>();
  private _createNewSurveyCommand$ = new EventEmitter();
  private _surveyCreationCommand$ = new EventEmitter<McSurveyRequest>();
  private _surveyDeleteCommand$ = new EventEmitter<number>();
  private _cpiChangeCommand$ = new EventEmitter<{
    survey_id: number;
    index: number;
    cpi: number;
  }>();
  private _cpiUpdateCommand$ = new EventEmitter<{
    survey: MultiCountrySurvey;
    cpi: number;
  }>();
  private _launchAllSurveysCommand$ = new EventEmitter();
  private _statusUpdateCommand$ = new EventEmitter();
  private _mcSameCountryExclusionsUpdate$ = new EventEmitter<{
    ps_survey_id: number;
    survey_localization: string;
  }>();
  private _applyTemplateCommand$ = new EventEmitter<number>();

  public loader: boolean = false;
  public isDraftSaved: boolean = false;
  public surveyStatus = SURVEY_STATUS;
  public surveyId?: number;
  public languages: LanguageInterface[] = [];
  public selectedCountry: CountryInterface = {} as CountryInterface;
  public isCPILoading: boolean = false;
  public isMcSurveyLoading: boolean = false;
  public isSetHeaderCpi: boolean = false;
  public manualProcessing: boolean = false;
  public isServiceBuyerOperation: boolean = false;
  public isLaunchSurveyPage: boolean = false;
  public isSurveyNotDraft: boolean = false;

  constructor(
    private _createSurveyService: CreateSurveyService,
    private _toastr: ToasterService,
    private _buyerApiService: BuyerApiService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _auth: AuthService,
    private _modal: ModalService,
    private _feasibilityComponentService: FeasibilityComponentService,
    private _qualificationService: QualificationsService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _multiCountryService: MultiCountryService,
    private _templateService: TemplateService,
    private _surveyPayloadService: SurveyPayloadService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  ngOnInit(): void {
    this._initiateStaticConfigs();
    this._handleRouteChanges();
    this._handleCpiLoading();
    this._handleManualProcessing();
    this._handleMcSurveySelection();
    this._handleMcFeasibilityCalls();
    this._handleMcCpiFeasDataUpdates();
    this._handleSurveyCreationProcess();
    this._handleNewSurveyCreation();
    this._handleSurveyDeletion();
    this._handleCpiUpdates();
    this._handleLaunchAllSurveys();
    this._handleStatusChangeForAllSurveys();
    this._handleSameCountryExclusionScenario();
    this._handleTemplateApplicationScenarios();
    this._handleMcSurveyLaunchSettings();
    this._handleUpdateAllSurveys();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.multiCountrySurveyData?.currentValue?.length >
        changes.multiCountrySurveyData?.previousValue?.length &&
      this.isLaunchSurveyPage
    ) {
      const surveyIds = changes.multiCountrySurveyData?.currentValue
        ?.filter(
          (survey: MultiCountrySurvey) =>
            !changes.multiCountrySurveyData.previousValue?.some(
              (previoudSurvey: MultiCountrySurvey) =>
                survey.survey_id === previoudSurvey.survey_id
            )
        )
        ?.map((survey: MultiCountrySurvey) => Number(survey.survey_id));
      this._multiCountryDraftService.setMultiCountryRunFeasibilityInfo({
        shouldSetFeasibilityFromFlyout: true,
        runFeasCalcForSurveys: surveyIds,
      });
    }
    if (changes.selectedTemplateId?.currentValue) {
      this._applyTemplateCommand$.next(
        changes.selectedTemplateId?.currentValue
      );
    }
  }

  private _initiateStaticConfigs(): void {
    this.isLaunchSurveyPage = this._router.url.includes('launch-survey');
    this._isOperator = this._auth.userType === 'operator';
    this.isServiceBuyerOperation = this._auth.isLoggedInAsServiceBuyer();
    this.isSurveyNotDraft =
      this.surveyDetails?.ps_survey_status !== this.surveyStatus.DRAFT;
  }

  private _handleRouteChanges(): void {
    this._subscriptions.add(
      this._activatedRoute.params
        .pipe(
          filter((params) => params && params.id),
          map((params) => Number(params.id))
        )
        .subscribe((surveyId: number) => {
          this.surveyId = surveyId;
        })
    );
  }

  private _handleCpiLoading(): void {
    this._subscriptions.add(
      this._surveyMetaDataService.getCPIData().subscribe((data) => {
        this.isCPILoading = data.loading;
        this._handleManualProcessing();
      })
    );
  }

  private _handleMcSurveySelection(): void {
    this._subscriptions.add(
      this._surveySelectionCommand$
        .pipe(
          filter(
            (selectedSurveyId) =>
              !this._shouldStopSurveySelectionProcess(selectedSurveyId)
          ),
          switchMap((selectedSurveyId) => {
            if (this._indexOfUpdatedSurveyIdInMcSurvey() > -1) {
              return this._updatePreviouslySelectedSurvey().pipe(
                filter((response) => !!response),
                map(() => selectedSurveyId)
              );
            }
            return of(selectedSurveyId);
          }),
          switchMap((selectedSurveyId) =>
            this._saveMcSurveyLaunchSettings().pipe(map(() => selectedSurveyId))
          )
        )
        .subscribe((selectedSurveyId) => {
          this._routeToTheSelectedSurvey(selectedSurveyId);
        })
    );
  }

  private _shouldStopSurveySelectionProcess(selectedSurveyId: number): boolean {
    if (this.surveyDetails?.ps_survey_id === selectedSurveyId) {
      return true;
    }

    return this._handleManualProcessBrowsingAnotherLocale();
  }

  private _handleUpdateAllSurveys() {
    this._subscriptions.add(
      merge(
        this._updateSurveysAndSelectCommand$.asObservable(),
        this._multiCountryDraftService
          .getMcTraditionalUpdateCommand()
          .pipe(map(() => null))
      )
        .pipe(
          switchMap((surveyId) => {
            if (!Object.values(this._allMcTraditionalSurveys).length) {
              return of({ msg: 'no-udate', surveyId });
            }
            if (
              this.surveyDetails?.ps_survey_status === this.surveyStatus.DRAFT
            ) {
              return of({ msg: 'update-all', surveyId });
            }

            return this._confirmUserBeforeApplyingChangesInLiveEdit().pipe(
              map((msg) => ({ msg, surveyId }))
            );
          }),
          switchMap(({ msg, surveyId }) => {
            if (msg === 'update-all') {
              return this._updateAllMcSurveys().pipe(
                tap(() => (this._allMcTraditionalSurveys = {})),
                map(() => surveyId)
              );
            }

            this._allMcTraditionalSurveys = {};
            return of(surveyId);
          }),
          filter((surveyId): surveyId is number => !!surveyId)
        )
        .subscribe((surveyId) => {
          this._surveySelectionCommand$.next(surveyId);
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcTraditionalSurveyRequest()
        .subscribe((surveys) => {
          this._allMcTraditionalSurveys = surveys;
        })
    );
  }

  private _confirmUserBeforeApplyingChangesInLiveEdit(): Observable<string> {
    return this._modal
      .open(DraftSurveyConfirmationModalComponent, {
        data: {
          headingText: 'Apply and save the changes',
          text: CreateSurveyConstants.CONFIRM_MODAL_TEXT
            .APPLY_CHANGES_CONFIRMATION,
          declineButtonLabel: 'Apply only in this locale',
          confirmButtonLabel: 'Apply to all locales',
        },
        width: '50%',
      })
      .onClose$.pipe(
        map((msg) => {
          if (msg === 'cancel') {
            return 'exit';
          }
          if (msg === 'no') {
            return 'update-one';
          }
          return 'update-all';
        }),
        filter((action) => ['update-one', 'update-all'].includes(action))
      );
  }

  private _updateAllMcSurveys(): Observable<null> {
    return of(this._allMcTraditionalSurveys).pipe(
      filter((allSurveysObj) => !!Object.values(allSurveysObj).length),
      switchMap((allSurveysObj) => {
        return from(Object.values(allSurveysObj)).pipe(
          filter(
            (surveyPayload) => surveyPayload.ps_survey_id !== this.surveyId
          ),
          mergeMap((surveyPayload) => {
            const payload = new McQuotaQualificationActions(
              this._allMcTraditionalSurveys,
              {} as Qualification,
              this._surveyPayloadService
            ).mapPayloadForMc(surveyPayload.ps_survey_id);
            payload.aida = new AidaSurveyPayloadTransformer(
              this._allMcTraditionalSurveys[
                surveyPayload.ps_survey_id
              ] as unknown as SurveyPayload,
              this._aidaPayloadService.getMappedDBQualificationCodes()
            ).transform();

            payload.qbp = this._getQbp(
              this._allMcTraditionalSurveys[
                surveyPayload.ps_survey_id
              ] as unknown as SurveyPayload
            );

            return this._buyerApiService
              .updateSurvey(payload, payload.ps_survey_id!)
              .pipe(
                catchError(({ error }) => {
                  this._toastr.error(error.ps_api_response_message, '', {
                    enableHtml: true,
                  });
                  return EMPTY;
                })
              );
          }),
          toArray(),
          map(() => null)
        );
      })
    );
  }

  private _getQbp(survey: SurveyPayload) {
    if (survey.ps_survey_status !== SURVEY_STATUS.DRAFT) {
      return survey.qbp;
    }
    const isMultiNestedQuotasAvailable = Common.isMultiNestedQuotasAvailable(
      survey?.quotas ?? []
    );
    return isMultiNestedQuotasAvailable ? { ...DEFAULT.QBP } : survey?.qbp;
  }

  private _updatePreviouslySelectedSurvey(): Observable<SurveyNoTargetingResponse | null> {
    const aida = new AidaSurveyPayloadTransformer(
      {
        ...this.surveyPayload,
        ps_survey_status: this.surveyDetails?.ps_survey_status,
      },
      this._aidaPayloadService.getMappedDBQualificationCodes()
    ).transform();
    this.surveyPayload.aida = aida;

    this.surveyPayload.qbp = this._getQbp(this.surveyPayload);

    const hasMcParent = this.multiCountrySurveyData.some(
      (mcSurvey) => !!mcSurvey.mc_parent
    );

    if (!hasMcParent) {
      this.surveyPayload.mc = {
        ...(this.surveyPayload.mc || {}),
        parent: true,
      };
    }

    const surveyPayload = this._surveyPayloadService.removeEmpty(
      { ...this.surveyPayload },
      CreateSurveyConstants.BLANK_PAYLOAD_EXCEPTION
    );

    return this._buyerApiService
      .updateSurvey(surveyPayload, surveyPayload.ps_survey_id!)
      .pipe(
        catchError((err) => {
          this._toastr.error(err.error.ps_api_response_message, '', {
            enableHtml: true,
          });
          return of(null);
        })
      );
  }

  private _routeToTheSelectedSurvey(surveyId: number): void {
    if (this.isLaunchSurveyPage) {
      this._router.navigateByUrl(`/launch-survey/${surveyId}`);
      return;
    }

    this._router.navigateByUrl(`/surveys/${surveyId}/edit`);
  }

  private _handleMcFeasibilityCalls(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryRunFeasibilityInfo()
        .pipe(
          filter(
            (data) =>
              data &&
              !!data.runFeasCalcForSurveys &&
              !!data.runFeasCalcForSurveys.length
          ),
          map((data) => data.runFeasCalcForSurveys),
          map((runFeasCallFor) =>
            this.multiCountrySurveyData.filter((mcSurvey) =>
              runFeasCallFor.includes(mcSurvey.survey_id)
            )
          )
        )
        .subscribe((mcSurveys) => {
          this._setFeasibilityPostDraftSave(mcSurveys);
        })
    );
  }

  private _handleMcCpiFeasDataUpdates(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryFeasCpiData()
        .pipe(
          map(
            (cpiFeasData) =>
              new MultiCountrySurveyFeasDataMapper(
                this.multiCountrySurveyData,
                cpiFeasData
              )
          ),
          filter((mapper) => !!mapper.find()),
          map((mapper) => mapper.transform())
        )
        .subscribe((mappedMcSurveys) => {
          this.updateMultiCountrySurveyData.emit(mappedMcSurveys);
        })
    );
  }

  private _handleManualProcessing() {
    // Deciding whether manual processig is on or not
    const processingSubscriber = this._surveyMetaDataService
      .getSurveyProcessing()
      .subscribe(({ manual }) => {
        if (typeof manual === 'boolean') {
          this.manualProcessing = manual;
        }
      });
    this._subscriptions.add(processingSubscriber);

    // When manual processing is loading or not, updating the same in the active survey
    this._subscriptions.add(
      this._createSurveyService
        .getIsManualProcessingPermission()
        .pipe(filter(() => this.manualProcessing))
        .subscribe((isPermission: boolean) => {
          this.multiCountrySurveyData.forEach((mcSurvey) => {
            if (this.surveyId === mcSurvey.survey_id) {
              mcSurvey.loading = !isPermission;
            }
          });
        })
    );
  }

  private async _setFeasibilityPostDraftSave(mcSurveys: MultiCountrySurvey[]) {
    mcSurveys.forEach(async (mcSurvey) => {
      // In case of manual processing, if data is not saved, do not proceed further for that survey
      if (
        this.manualProcessing &&
        this.isCPILoading &&
        mcSurvey.manualProcessingLoading
      ) {
        return;
      }
      const [surveyId, language, surveyRequest, qualifications] =
        this._buildPayloadForSetFeasibility(mcSurvey);
      await this._feasibilityComponentService.setFeasibility(
        surveyId,
        language,
        surveyRequest,
        qualifications,
        true,
        mcSurvey.survey_id === this.surveyId
      );
    });
  }

  private _buildPayloadForSetFeasibility(mcSurvey: MultiCountrySurvey) {
    const surveyId = Number(mcSurvey.survey_id);
    const language = mcSurvey.language_short_code;
    const qualifications = this._qualificationService.getQualifications();
    return [surveyId, language, mcSurvey, qualifications];
  }

  public openMultiCountryDraftModal() {
    this.selectionChange.emit();
  }

  public onSurveyDelete(mcSurvey: MultiCountrySurvey): void {
    this._surveyDeleteCommand$.next(mcSurvey.survey_id);
  }

  private _handleSurveyDeletion(): void {
    this._subscriptions.add(
      this._surveyDeleteCommand$
        .pipe(
          switchMap((surveyId) =>
            this._openSurveyDeleteModalAndConfirm(surveyId)
          )
        )
        .subscribe((surveyId) => {
          const formerIndex = this.multiCountrySurveyData.findIndex(
            (mcSurvey) => mcSurvey.survey_id === surveyId
          );
          this._removeDeletedSurveyFromTheList(surveyId);
          // Select the new survey if the selected survey is deleted
          const nextIndex =
            this._findIndexOfSurveyToSelectPostDelete(formerIndex);
          const newSurvey = this.multiCountrySurveyData[nextIndex];
          this._surveySelectionCommand$.next(newSurvey.survey_id);
          this._toastr.success(
            notifyMessage.successMessage.MULTICOUNTRY_DRAFT.SURVEY_DELETED
          );
        })
    );
  }

  private _removeDeletedSurveyFromTheList(deletedSurveyId: number): void {
    this.multiCountrySurveyData = this.multiCountrySurveyData.filter(
      (mcSurvey) => mcSurvey.survey_id !== deletedSurveyId
    );

    // Remove delete survey from group
    const grouping = this.surveyPayload.survey_grouping || [];
    this.surveyPayload.survey_grouping = grouping.filter(
      (id) => id !== deletedSurveyId
    );

    this._multiCountryDraftService.sendMcTraditionalSettingsForRemoval(
      deletedSurveyId
    );
    this.updateMultiCountrySurveyData.emit(this.multiCountrySurveyData);
  }

  private _openSurveyDeleteModalAndConfirm(
    surveyId: number
  ): Observable<number> {
    return of(surveyId).pipe(
      map(() => ({
        surveyId,
        toDelete: true,
        isMc: true,
      })),
      map((data) =>
        this._modal.open(ChangeSurveyStatusModalComponent, {
          data,
          width: '500px',
          height: '21rem',
        })
      ),
      switchMap((ref) => ref.onClose$),
      filter((msg) => msg === Constants.MODAL_MESSAGE.DELETE_SURVEY),
      map(() => surveyId)
    );
  }

  private _findIndexOfSurveyToSelectPostDelete(formerIndex: number) {
    // When manual Processing is enabled and CPI/feasibility is still loading (not clicked on 'Save & Process' yet)
    let newIndex = -1;
    if (this.manualProcessing && this.isCPILoading) {
      newIndex = this.multiCountrySurveyData.findIndex(
        (mcSurvey) => mcSurvey.loading
      );
    }
    if (newIndex !== -1) {
      return newIndex;
    }
    return formerIndex > this.multiCountrySurveyData.length - 1
      ? 0
      : formerIndex;
  }

  private _modifyStringForBuyerRole(string: string) {
    if (this._isOperator) {
      return string.replace(/Project Manager/g, 'Buyer Project Manager');
    }

    return string;
  }

  public onSurveySelection(survey: MultiCountrySurvey) {
    if (this._hasInvalidUniqueLinks(this.surveyPayload)) {
      this._showUniqueLinksError();
      return;
    }
    this._updateSurveysAndSelectCommand$.next(survey.survey_id);
  }

  private _handleManualProcessBrowsingAnotherLocale() {
    if (this.manualProcessing && this.isCPILoading) {
      this._toastr.warning(
        notifyMessage.warningMessage.MULTICOUNTRY
          .MANUAL_PROCESSING_BROWSE_ANOTHER_COUNTRY
      );
      return true;
    }
    return false;
  }

  public selectCountry(country: CountryInterface) {
    this.selectedCountry = country;
    this.languages = country.lang;
    this.setLanguage(this.languages[0]);
  }

  public setLanguage(language: LanguageInterface) {
    this._selectedLanguage = language;
  }

  public onNewSurveyCreation(): void {
    if (
      this.surveyPayload.unique_links &&
      this.surveyPayload.is_same_file_entry_links
    ) {
      const mcSurveys = [
        ...this.multiCountrySurveyData,
        this.multiCountrySurveyData[0],
      ];
      if (!this._validateUniqueBuyerEntryLinks(mcSurveys)) {
        return; // Stop execution if validation fails
      }
    }
    this._createNewSurveyCommand$.next();
  }

  private _handleNewSurveyCreation(): void {
    this._subscriptions.add(
      this._createNewSurveyCommand$
        .pipe(
          filter(() => this._shouldAllowCreationOfNewSurvey()),
          map(() => {
            const [firstSurvey] = this.multiCountrySurveyData;
            const firstMcSurvey = this._isCheckedSameEntryLink(firstSurvey);
            return new MultiCountryNewSurveyRequestMapper(
              firstMcSurvey,
              this.selectedCountry,
              this._selectedLanguage,
              this._auth.isLoggedInAsServiceBuyer()
            ).transform();
          })
        )
        .subscribe((surveyRequest) => {
          this._surveyCreationCommand$.next(surveyRequest);
        })
    );
  }

  private _handleSurveyCreationProcess(): void {
    this._subscriptions.add(
      this._surveyCreationCommand$
        .pipe(
          filter(
            (surveyRequest) => !this._doesLocaleAlreadyExist(surveyRequest)
          ),
          switchMap((surveyRequest) =>
            this._multiCountryService.addSurvey(surveyRequest)
          ),
          map(
            (survey) =>
              <
                [
                  {
                    response: SurveyNoTargetingResponse;
                    payload: McSurveyRequest;
                  }[],
                  { error: any; payload: McSurveyRequest }[]
                ]
              >[
                [survey].filter((survey) => survey.hasOwnProperty('response')),
                [survey].filter((survey) => survey.hasOwnProperty('error')),
              ]
          ),
          filter(([, [err]]) => {
            if (err) {
              const {
                error: {
                  error: { ps_api_response_message: errMessage = '' } = {},
                } = {},
              } = err;
              this._toastr.error(errMessage);
              return false;
            }

            this._toastr.success(
              notifyMessage.successMessage.MULTICOUNTRY_DRAFT.NEW_SURVEY_ADDED
            );

            return true;
          }),
          map(
            ([[{ response: createdSurveyResponse }]]) => createdSurveyResponse
          )
        )
        .subscribe((createdSurveyResponse) => {
          const { ps_survey_id, survey_localization } = createdSurveyResponse;
          const data = {
            ps_survey_id,
            survey_localization,
          };
          this._mcSameCountryExclusionsUpdate$.next(data);
          this._surveySelectionCommand$.next(ps_survey_id);
        })
    );
  }

  private _shouldAllowCreationOfNewSurvey(): boolean {
    if (!this.selectedCountry.short_Code || !this._selectedLanguage) {
      this._toastr.error(notifyMessage.errorMessage.COUNTRY_MISSING);
      return false;
    }

    const stopDueToManualProcessing =
      this._handleManualProcessBrowsingAnotherLocale();
    if (stopDueToManualProcessing) {
      return false;
    }

    return true;
  }

  private _doesLocaleAlreadyExist(payload: McSurveyRequest): boolean {
    const localeExists = this.multiCountrySurveyData.some(
      (mcSurvey) => mcSurvey.survey_localization === payload.survey_localization
    );

    if (localeExists) {
      this._toastr.error(
        notifyMessage.errorMessage.MULTICOUNTRY.LOCALE_ALREADY_EXISTS.replace(
          '<LOCALE>',
          `${payload.country_name}-${payload.language_code}`
        )
      );
      return true;
    }

    return false;
  }

  private _handleCpiUpdates(): void {
    this._subscriptions.add(
      this._cpiChangeCommand$.pipe().subscribe(({ survey_id, index, cpi }) => {
        if (!this._isHighCpi(cpi)) {
          this.hideCpiConfirmationRow(index);
          this._updateMultiCountryData(survey_id, { state: { cpi: 'edit' } });
          return;
        }

        this.showCpiConfirmationRow(index);
        this._updateMultiCountryData(survey_id, {
          state: { cpi: 'not-confirmed' },
        });
      })
    );

    this._subscriptions.add(
      this._cpiUpdateCommand$
        .pipe(
          filter(({ survey }) => {
            if (survey.state.cpi === 'not-confirmed') {
              this._toastr.error(notifyMessage.CPI_CONFIRMATION_ERROR);
              return false;
            }
            return true;
          }),
          switchMap((data) =>
            this._buyerApiService
              .updateOfferPriceOfSurvey(data.survey.survey_id, data.cpi)
              .pipe(
                map(() => data),
                catchError((err) => {
                  this._toastr.error(err.error?.ps_api_response_message);
                  data.survey.state = {
                    cpi: 'view',
                  };
                  return EMPTY;
                })
              )
          )
        )
        .subscribe(({ survey, cpi }) => {
          this._updateMultiCountryData(survey.survey_id, {
            cpi,
            price_override: cpi,
            state: {
              cpi: 'view',
            },
          });
          this._multiCountryDraftService.setMultiCountryRunFeasibilityInfo({
            shouldSetFeasibilityFromFlyout: true,
            runFeasCalcForSurveys: [survey.survey_id],
          });
        })
    );
  }

  private _isHighCpi(cpi: number): boolean {
    if (cpi > Number(LayoutConstants.MAX_CPI_THRESHOLD)) {
      return true;
    }
    return false;
  }

  public showCpiConfirmationRow(index: number): void {
    this.expandable?.expand(index);
  }

  public hideCpiConfirmationRow(index: number): void {
    this.expandable?.retract(index);
  }

  private _updateMultiCountryData(
    surveyId: number,
    partial: Partial<MultiCountrySurvey>
  ): void {
    this.multiCountrySurveyData = this.multiCountrySurveyData.map((survey) => ({
      ...survey,
      ...(survey.survey_id === surveyId && partial),
    }));
    this.updateMultiCountrySurveyData.emit(this.multiCountrySurveyData);
  }

  public onCpiChange(survey: MultiCountrySurvey, index: number, cpi: string) {
    this._cpiChangeCommand$.next({
      survey_id: survey.survey_id,
      index,
      cpi: Number(cpi),
    });
  }

  public onCpiUpdate(survey: MultiCountrySurvey, cpi: string): void {
    this._cpiUpdateCommand$.next({ survey, cpi: Number(cpi) });
  }

  public onCpiConfirmation(survey: MultiCountrySurvey, index: number): void {
    this._updateMultiCountryData(survey.survey_id, { state: { cpi: 'edit' } });
    this.hideCpiConfirmationRow(index);
  }

  private _handleLaunchAllSurveys(): void {
    this._subscriptions.add(
      this._launchAllSurveysCommand$
        .pipe(switchMap(() => this._saveMcSurveyLaunchSettings()))
        .pipe(switchMap(() => this._updatePreviouslySelectedSurvey()))
        .subscribe(() => {
          this._statusUpdateCommand$.next();
        })
    );
  }

  private _saveMcSurveyLaunchSettings(): Observable<unknown> {
    if (!this._mcLaunchSettings.length) {
      return of({});
    }

    return forkJoin(
      this._mcLaunchSettings
        .filter((setting) =>
          this.multiCountrySurveyData.some(
            (survey) => survey.survey_id === setting.survey_id
          )
        )
        .map((setting) => this._patchSurvey(setting))
    ).pipe(
      catchError((err) => {
        this._toastr.error(err.message);
        return of(null);
      }),
      filter((response) => !!response)
    );
  }

  private _patchSurvey(setting: McTraditionalSettings) {
    return this._buyerApiService
      .patchSurvey(setting.survey_id, setting.data)
      .pipe(
        catchError((error) =>
          throwError(new SurveyUpdateError(error, this._auth.buyerConfig!))
        )
      );
  }

  private _handleStatusChangeForAllSurveys(): void {
    this._subscriptions.add(
      this._statusUpdateCommand$
        .pipe(switchMap(() => this._launchAllSurveys()))
        .subscribe((launchedSurveyData) => {
          const newlyLaunchedSurveyIds = launchedSurveyData
            .filter((item) => !!item)
            .map((item) => Number(item?.payload.survey_id));
          this._updateStatusInMcSurveysIfLaunched(newlyLaunchedSurveyIds);
          const surveysStillInDraft = this.multiCountrySurveyData.filter(
            (mcSurey) => mcSurey.ps_survey_status === SURVEY_STATUS.DRAFT
          );
          if (!surveysStillInDraft.length) {
            this._toastr.success(
              notifyMessage.successMessage.MULTICOUNTRY_DRAFT.LAUNCH_SUCCESS
            );
            this._multiCountryDraftService.resetMultiCountryData();
            this._router.navigateByUrl('/dashboard');
          }
        })
    );
  }

  private _launchAllSurveys() {
    return from(this.multiCountrySurveyData).pipe(
      filter((mcSurey) => mcSurey.ps_survey_status === SURVEY_STATUS.DRAFT),
      mergeMap((mcSurey) =>
        this._multiCountryService.updateSurveyStatus(
          mcSurey,
          SURVEY_STATUS.LIVE
        )
      ),
      filter((result) => !!result),
      toArray()
    );
  }

  public onLaunchAllSurveys(): void {
    if (this._hasInvalidSurveyConditions()) {
      return;
    }
    this._launchAllSurveysCommand$.next();
  }

  private _updateStatusInMcSurveysIfLaunched(
    launchedSurveyIds: number[]
  ): void {
    this.multiCountrySurveyData.forEach((mcSuvey) => {
      const isLaunched = launchedSurveyIds.some(
        (launchedSurveyId) => launchedSurveyId === mcSuvey.survey_id
      );
      if (isLaunched) {
        this._updateMultiCountryData(mcSuvey.survey_id, {
          ps_survey_status: SURVEY_STATUS.LIVE,
        });
      }
    });
  }

  private _handleSameCountryExclusionScenario(): void {
    this._subscriptions.add(
      this._mcSameCountryExclusionsUpdate$
        .pipe(
          map(
            (currentSurvey) =>
              <
                [
                  { ps_survey_id: number; survey_localization: string },
                  { ps_survey_id: number; survey_localization: string }[]
                ]
              >[
                currentSurvey,
                this.multiCountrySurveyData.map((s) => ({
                  ps_survey_id: s.survey_id,
                  survey_localization: s.survey_localization,
                })),
              ]
          ),
          map(
            ([currentSurvey, data]) =>
              <[number, PatchSurveyInterface[]]>[
                currentSurvey.ps_survey_id,
                new MultiCountrySameCountryExclusionPayloadMapper(
                  data,
                  currentSurvey
                ).transform(),
              ]
          ),
          filter(([, payload]) => !!(payload[0].value as number[]).length),
          mergeMap(([surveyId, payload]) =>
            this._buyerApiService.patchSurvey(surveyId, payload)
          )
        )
        .subscribe()
    );
  }

  private _handleTemplateApplicationScenarios(): void {
    const templateResponse$ = this._applyTemplateCommand$.pipe(
      filter((templateId) => !!templateId),
      filter(() => !this._stopApplyingTemplate()),
      switchMap((templateId) => this._templateService.getById(templateId)),
      shareReplay(1)
    );

    // Applying SC template
    this._subscriptions.add(
      templateResponse$
        .pipe(
          filter((templateResponse) => !templateResponse.is_mc_template),
          map(
            (templateResponse) =>
              <
                [
                  TemplateResponse,
                  { modal_text: string; delete_survey_id?: number }
                ]
              >[
                templateResponse,
                new MCTemplateSelectionDataMapper(
                  this.multiCountrySurveyData,
                  this.surveyDetails?.ps_survey_id!,
                  templateResponse.template.survey_localization
                ).map(),
              ]
          )
        )
        .subscribe(
          ([
            templateResponse,
            { modal_text: modalText, delete_survey_id: deleteSurveyId },
          ]) => {
            this._handleSCTemplateApplication(
              templateResponse.template,
              modalText,
              deleteSurveyId
            );
          }
        )
    );

    // Applying MC template
    this._subscriptions.add(
      templateResponse$
        .pipe(
          filter(
            (templateResponse) =>
              !!templateResponse.is_mc_template &&
              !!templateResponse.template.mc_template_id
          )
        )
        .subscribe((templateResponse) => {
          this._handleMCTemplateApplication(templateResponse.template);
        })
    );
  }

  private _stopApplyingTemplate() {
    if (this.manualProcessing && this.isCPILoading) {
      this._toastr.warning(
        notifyMessage.warningMessage.MULTICOUNTRY
          .MANUAL_PROCESSING_BROWSE_ANOTHER_COUNTRY
      );
      return true;
    }
    return false;
  }

  private _handleSCTemplateApplication(
    template: Template,
    modalText: string,
    deleteSurveyId?: number
  ): void {
    this._subscriptions.add(
      this._modal
        .open(ConfirmationModalComponent, {
          data: {
            text: modalText,
            confirmButtonLabel: 'Yes',
          },
          width: '60%',
        })
        .onClose$.pipe(
          tap((msg) => {
            if (msg === 'cancel') {
              this.cancelTemplateSelection.emit();
            }
          }),
          filter((msg) => msg === 'ok'),
          switchMap((msg) => {
            if (this._isDeleteSurveyScenario(modalText)) {
              return this._deleteMcSurvey(deleteSurveyId!).pipe(
                map(() => of(msg))
              );
            }
            return of(msg);
          }),
          map(() =>
            new MultiCountryTemplateBasedSurveyRequestMapper(
              this.multiCountrySurveyData,
              this.surveyDetails?.ps_survey_id!,
              template,
              this.allCountries,
              this._auth.isLoggedInAsServiceBuyer()
            ).transform()
          )
        )
        .subscribe((surveyRequest) => {
          this._surveyCreationCommand$.next(surveyRequest);
        })
    );
  }

  private _isDeleteSurveyScenario(type: string) {
    return [
      CreateSurveyConstants.CONFIRM_MODAL_TEXT.TEMPLATE_SELECTION_FOR_MC
        .SAME_LOCALE_AS_SELECTED,
      CreateSurveyConstants.CONFIRM_MODAL_TEXT.TEMPLATE_SELECTION_FOR_MC
        .NOT_SAME_LOCALE_AS_SELECTED,
    ].includes(type);
  }

  private _deleteMcSurvey(surveyId: number) {
    return this._buyerApiService
      .deleteDraftSurvey(surveyId)
      .pipe(tap(() => this._removeDeletedSurveyFromTheList(surveyId)));
  }

  private _handleMCTemplateApplication(template: Template): void {
    this._subscriptions.add(
      this._modal
        .open(DraftSurveyConfirmationModalComponent, {
          data: {
            text: CreateSurveyConstants.CONFIRM_MODAL_TEXT
              .TEMPLATE_SELECTION_FOR_MC.MC_SURVEY_AND_MC_TEMPLATE_SELECTED,
            confirmButtonLabel: 'Yes, delete the surveys',
            declineButtonLabel: 'No keep surveys as a draft',
          },
        })
        .onClose$.pipe(
          tap((msg) => {
            if (msg === 'cancel') {
              this.cancelTemplateSelection.emit();
            }
          }),
          filter((msg) => msg !== 'cancel'),
          switchMap((msg) => {
            if (msg === 'yes') {
              return this._multiCountryService.deleteMcSurveys(
                this.multiCountrySurveyData
              );
            }
            return of({});
          }),
          switchMap(() =>
            this._templateService.createMcSurveysUsingTemplate(
              template.mc_template_id!
            )
          )
        )
        .subscribe((response) => {
          if (response.failed_locales?.length) {
            this._toastr.error(
              `${
                notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.REJECTED_SURVEYS
              } ${new LocaleFullNameMapper(
                response.failed_locales,
                this.allCountries
              ).map()}`
            );
          }

          if (response.survey_ids?.length) {
            this._toastr.success(
              notifyMessage.successMessage.MULTICOUNTRY_DRAFT.NEW_SURVEYS_ADDED
            );
            this.updateMultiCountrySurveyData.emit([]);
            this._routeToTheSelectedSurvey(response.survey_ids[0]);
          }
        })
    );
  }

  private _handleMcSurveyLaunchSettings(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcTraditionalSettings()
        .pipe(skip(1))
        .subscribe((settings) => {
          this._mcLaunchSettings = settings;
        })
    );
  }

  private _indexOfUpdatedSurveyIdInMcSurvey() {
    return this.multiCountrySurveyData.findIndex(
      (mcSurvey) => mcSurvey.survey_id === this.surveyPayload.ps_survey_id
    );
  }

  private _hasInvalidSurveyConditions(): boolean {
    if (this._hasInvalidUniqueLinks(this.surveyPayload)) {
      this._showUniqueLinksError();
      return true;
    }
    if (
      !this.surveyPayload.live_url &&
      !this.surveyPayload?.unique_links?.enable
    ) {
      this._toastr.error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL
      );
      return true;
    }

    return false;
  }

  private _hasInvalidUniqueLinks(surveyPayload: SurveyPayload): boolean {
    return !!(
      surveyPayload.unique_links?.enable &&
      !surveyPayload.unique_links?.items?.length
    );
  }

  private _showUniqueLinksError(): void {
    this._toastr.error(
      notifyMessage.errorMessage.SURVEY_LAUNCH.UNIQUE_BUYER_LINKS_NOT_UPLOAD
    );
  }

  private _isCheckedSameEntryLink(mcSurvey: MultiCountrySurvey) {
    if (!this.surveyPayload.is_same_buyer_url_checked) {
      delete mcSurvey?.live_url;
      delete mcSurvey?.test_url;
    }
    return mcSurvey;
  }

  private _validateUniqueBuyerEntryLinks(mcsurveys: MultiCountrySurvey[]) {
    const uniqueLinks = this.surveyPayload?.unique_links;
    if (!uniqueLinks?.items?.length) {
      return true;
    }
    const expectedLinks = mcsurveys.reduce((total, mcSurvey) => {
      return (
        total +
        Math.floor((mcSurvey.completes_required / mcSurvey.expected_ir) * 100)
      );
    }, 0);
    if (uniqueLinks.items.length < expectedLinks) {
      this._toastr.error(
        notifyMessage.errorMessage.UNIQUE_BUYER_LINKS.UPLOAD_UNIQUE_LINKS.replace(
          '<expectedLinks>',
          String(expectedLinks)
        )
      );
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
